import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { api } from "../lib/api";

interface JobDetail {
  id: string;
  request: {
    id: string;
    rawText: string;
    status: string;
    city?: string | null;
    scheduledFor?: string | null;
    category?: {
      nameRo: string;
      nameRu?: string | null;
    } | null;
    client: {
      displayName?: string | null;
      firstName?: string | null;
      telegramUsername?: string | null;
      phone?: string | null;
    } | null;
    clientProfile: {
      ratingAvg: string;
      ratingCount: number;
    } | null;
  } | null;
}

export function HandymanJobDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { locale, t } = useLocale();
  const [job, setJob] = useState<JobDetail | null>(null);

  useEffect(() => {
    if (!requestId) return;
    api.get<JobDetail>(`/handymen/me/jobs/${requestId}`).then(({ data }) => setJob(data));
  }, [requestId]);

  const formatDateTime = (value?: string | null) => {
    if (!value) return t.anytime;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t.anytime;
    return date.toLocaleString();
  };

  if (!job?.request) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-sm text-gray-400">{t.loading}</div>
      </div>
    );
  }

  const clientName = job.request.client?.displayName || job.request.client?.firstName || t.guest;
  const initials = clientName.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
  const categoryName = job.request.category
    ? locale === "ru"
      ? job.request.category.nameRu || job.request.category.nameRo
      : job.request.category.nameRo
    : "-";
  const telegramLink = job.request.client?.telegramUsername
    ? `https://t.me/${job.request.client.telegramUsername}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b border-gray-100 bg-white px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate("/")} className="text-sm font-semibold text-gray-500">
            {t.back}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="text-2xl font-bold text-gray-900">{t.currentJobs}</div>
      </div>

      <div className="p-5">
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          {/* Client info */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-sm font-bold text-orange-600">
              {initials}
            </div>
            <div className="flex-1">
              <div className="font-bold text-gray-900">{clientName}</div>
              <div className="text-xs text-gray-500">
                {t.clientRating}: {job.request.clientProfile
                  ? `★ ${Number(job.request.clientProfile.ratingAvg).toFixed(1)} (${job.request.clientProfile.ratingCount})`
                  : "—"}
              </div>
            </div>
            <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              {t.requestStatusLabels[job.request.status as keyof typeof t.requestStatusLabels] ?? job.request.status}
            </div>
          </div>

          {/* Request text */}
          <div className="rounded-xl bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-700">
            {job.request.rawText}
          </div>

          {/* Details */}
          <div className="mt-4 grid gap-1.5 text-sm text-gray-500">
            <div>🔧 {t.requestCategory}: <span className="font-medium text-gray-700">{categoryName}</span></div>
            <div>📍 {t.city}: <span className="font-medium text-gray-700">{job.request.city || "-"}</span></div>
            <div>🕐 {t.scheduledAt}: <span className="font-medium text-gray-700">{formatDateTime(job.request.scheduledFor)}</span></div>
          </div>

          {/* Actions */}
          <div className="mt-5 flex flex-wrap gap-2.5">
            {telegramLink ? (
              <a
                href={telegramLink}
                target="_blank"
                rel="noreferrer"
                className="flex-1 rounded-xl bg-orange-500 px-4 py-3 text-center text-sm font-semibold text-white active:bg-orange-600 transition-colors"
              >
                ✈ {t.openTelegram}
              </a>
            ) : null}
            {job.request.client?.phone ? (
              <a
                href={`tel:${job.request.client.phone}`}
                className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700 active:bg-gray-50 transition-colors"
              >
                📞 {t.callPhone}
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
